import { useStore } from 'vuex'
import Sortable from 'sortablejs'
import { EventBus } from '@/event-bus/event-bus'
import { computed, ref, useTemplateRef } from 'vue'

export const useSortFields = () => {
  const store = useStore()
  const workflow = computed(() => store.state.workflow.workflow)

  const fieldsRef = useTemplateRef('fieldsRef')
  const subFieldsRef = useTemplateRef('subFieldsRef')
  const sortFieldsMap = ref({})

  const createSortable = (field, endCallback) => {
    return new Sortable(field, {
      draggable: '.sortableRow',
      handle: '.sortHandle',
      animation: 150,
      scroll: true,
      forceFallback: true,
      scrollSensitivity: 100,
      onStart: function () {
        Array.prototype.forEach.call(
          document.querySelectorAll('.v-row-group__header'),
          function (el, index) {
            if (!el.classList.contains('sortableRow') && index !== 0) {
              el.classList.add('sortableRow')
            }
          }
        )
        Array.prototype.forEach.call(
          document.querySelectorAll('.sortableRow:not(.sortable-chosen)'),
          function (el, index) {
            if (!el.classList.contains('white-background-on-hover') && index !== 0) {
              el.classList.add('white-background-on-hover')
            }
          }
        )
      },
      onEnd: endCallback
    })
  }

  const moveField = (eventId, fromIndex, toIndex, modelIndex = 0) => {
    const event = workflow.value.eventsJson?.find((event) => event.id === eventId)
    const moveFields = (event) => {
      const field = event.dataModels[modelIndex].dataFields[fromIndex]
      event.dataModels[modelIndex].dataFields.splice(fromIndex, 1)
      event.dataModels[modelIndex].dataFields.splice(toIndex, 0, field)
    }
    EventBus.$emit('update-data-model-with-function', { updateFunction: moveFields, eventId: event.id })
  }

  const moveSubField = (eventId, fromIndex, toIndex, modelIndex = 0, fieldIndex) => {
    const event = workflow.value.eventsJson?.find((event) => event.id === eventId)
    const moveSubFields = (event) => {
      const subFields = event.dataModels[modelIndex].dataFields[fieldIndex].dataFields
      const [movedField] = subFields.splice(fromIndex, 1)
      subFields.splice(toIndex, 0, movedField)
      event.dataModels[modelIndex].dataFields[fieldIndex].dataFields = subFields
    }
    EventBus.$emit('update-data-model-with-function', { updateFunction: moveSubFields, eventId: event.id })
  }

  const initSortable = () => {
    destroySortable()

    if (window.Sortable) {
      window.Sortable.active = null
      window.Sortable.dragged = null
      window.Sortable.ghost = null
      window.Sortable.clone = null
      window.Sortable.from = null
      window.Sortable.lastDownEl = null
    }

    const activeUniqueIds = new Set()
    const fields = !fieldsRef.value ? [] : (Array.isArray(fieldsRef.value) ? fieldsRef.value : [fieldsRef.value])
    const subFields = !subFieldsRef.value ? [] : (Array.isArray(subFieldsRef.value) ? subFieldsRef.value : [subFieldsRef.value])

    fields.forEach(($field) => {
      const { eventId, modelIndex } = $field.dataset
      const uuid = `${eventId}-${modelIndex}`
      activeUniqueIds.add(uuid)

      let sortable = sortFieldsMap.value[uuid]

      if (sortable && !sortable.el?.isConnected) {
        sortable.destroy()
        delete sortFieldsMap.value[uuid]
        sortable = null
      }

      if (!sortable) {
        sortable = createSortable($field, (event) =>
          moveField(eventId, event.oldIndex, event.newIndex, parseInt(modelIndex))
        )
        sortFieldsMap.value[uuid] = sortable
      }
    })

    const nowTimeStamp = Date.now()

    subFields.forEach(($field) => {
      const { eventId, modelIndex, fieldIndex } = $field.dataset
      const uuid = `${eventId}-${modelIndex}-${fieldIndex}-${nowTimeStamp}`
      activeUniqueIds.add(uuid)

      let sortable = sortFieldsMap.value[uuid]

      if (sortable && !sortable.el?.isConnected) {
        sortable.destroy()
        delete sortFieldsMap.value[uuid]
        sortable = null
      }

      if (!sortable) {
        sortable = createSortable($field, (event) =>
          moveSubField(eventId, event.oldIndex, event.newIndex, parseInt(modelIndex), parseInt(fieldIndex))
        )
        sortFieldsMap.value[uuid] = sortable
      }
    })

    Object.keys(sortFieldsMap.value).forEach((uuid) => {
      if (!activeUniqueIds.has(uuid)) {
        sortFieldsMap.value[uuid].destroy()
        delete sortFieldsMap.value[uuid]
      }
    })
  }

  const destroySortable = () => {
    Object.keys(sortFieldsMap.value).forEach((uuid) => {
      const sortable = sortFieldsMap.value[uuid]
      if (sortable && typeof sortable.destroy === 'function') {
        sortable.destroy()
      }
      delete sortFieldsMap.value[uuid]
    })

    document.querySelectorAll('.sortable-drag, .sortable-ghost, .sortable-fallback')
      .forEach(el => el.remove())
  }

  return { fieldsRef, subFieldsRef, initSortable, destroySortable, createSortable }
}
